import { Formik } from 'formik';
import { lazy, Suspense } from 'react';

import { useHeader } from 'context';
import { ButtonColors } from 'types';
import { isMobile, isDesktop, useNavigate } from 'hooks';
import { makeSelectOptionsFromEntities } from 'utils/helpers';
import { useListQuery as useEventsQuery } from 'store/api/event';
import { useListQuery as useStoragesQuery } from 'store/api/storage';
import { useListQuery as useCompaniesQuery } from 'store/api/company';
import { useListQuery as useCarriersQuery } from 'store/api/carriers';
import {
  StockTransfer,
  StockTransferType,
  StockTransferStatus,
  stockTransferTypeOptions,
} from 'store/api/stock-transfers/types';
import {
  Row,
  Col,
  Icon,
  Flex,
  Input,
  Button,
  FormFooter,
  InputField,
  ButtonTypes,
  SelectField,
  CommonStyles,
  RadioFieldGroup,
  DatePickerField,
  SelectFieldProps,
} from 'components';

import * as Styles from './styles';
import { FormProps } from './types';
import { validationSchema } from './config';
import { StorageProductsSelectSection } from './storage-products-select-section';

// T-L09 (owner rule L14): the "For events" split, loaded only when a
// Storage -> Storage transfer is on screen.
const TransferEventSplit = lazy(() =>
  import('components/stock-transfer-event-split').then((module) => ({
    default: module.TransferEventSplit,
  }))
);

export const Form = <T extends StockTransfer>(props: FormProps<T>) => {
  const mobile = isMobile();
  const desktop = isDesktop();
  const navigate = useNavigate();

  const isEdit = 'id' in props.initialValues;
  const isCompleted =
    props?.initialValues?.status === StockTransferStatus.Completed;

  const { data: storages } = useStoragesQuery();
  const { data: companies } = useCompaniesQuery();
  const { data: carriers } = useCarriersQuery();
  const { data: events } = useEventsQuery({ limit: 10000, status: 'accepted' });

  useHeader({
    showBackButton: true,
    pageName: `${isEdit ? 'Edit' : 'New'} transfer`,
  });

  const eventSelectOptions =
    events?.rows?.map((event) => ({
      label: event.name,
      value: event.storage?.id,
    })) ?? [];
  const storageSelectOptions = makeSelectOptionsFromEntities(
    storages?.filter(({ type }) => type === 'storage')
  );
  const companySelectOptions = makeSelectOptionsFromEntities(companies);
  const carrierSelectOptions = makeSelectOptionsFromEntities(carriers);

  return (
    <Formik {...props} enableReinitialize validationSchema={validationSchema}>
      {({ dirty, values, isSubmitting, setFieldValue }) => {
        let sourceSelectFieldProps: Partial<SelectFieldProps> = {
          name: '',
        };
        let destinationSelectFieldProps: Partial<SelectFieldProps> = {
          name: '',
        };

        if (values.type === StockTransferType.EventToStorage) {
          sourceSelectFieldProps = {
            label: 'From: Event',
            options: eventSelectOptions,
          };

          destinationSelectFieldProps = {
            label: 'To: Storage',
            options: storageSelectOptions,
          };
        }

        if (values.type === StockTransferType.StorageToStorage) {
          sourceSelectFieldProps = {
            label: 'From: Storage',
            options: storageSelectOptions.filter(
              (option) => option.value !== values.destinationStorageId
            ),
          };

          destinationSelectFieldProps = {
            label: 'To: Storage',
            options: storageSelectOptions.filter(
              (option) => option.value !== values.sourceStorageId
            ),
          };
        }

        if (values.type === StockTransferType.StorageToEvent) {
          sourceSelectFieldProps = {
            label: 'From: Storage',
            options: storageSelectOptions,
          };

          destinationSelectFieldProps = {
            label: 'To: Event',
            options: eventSelectOptions,
          };
        }

        if (values.type === StockTransferType.ClientToClient) {
          sourceSelectFieldProps = {
            isDisabled: true,
            label: 'From: Client',
            name: 'sourceCompanyId',
            options: companySelectOptions.filter(
              (option) => option.value !== values.destinationCompanyId
            ),
          };

          destinationSelectFieldProps = {
            label: 'To: Client',
            name: 'destinationCompanyId',
            options: companySelectOptions.filter(
              (option) => option.value !== values.sourceCompanyId
            ),
          };
        }

        if (values.type === StockTransferType.EventToEvent) {
          sourceSelectFieldProps = {
            label: 'From: Event',
            options: eventSelectOptions.filter(
              (option) => option.value !== values.destinationStorageId
            ),
          };

          destinationSelectFieldProps = {
            label: 'To: Event',
            options: eventSelectOptions.filter(
              (option) => option.value !== values.sourceStorageId
            ),
          };
        }

        return (
          <CommonStyles.Form>
            <CommonStyles.FormInner>
              {!isCompleted && (
                <>
                  <Row>
                    <Col>
                      {!desktop ? (
                        <SelectField
                          onlyValue
                          name="type"
                          label="Type"
                          options={stockTransferTypeOptions}
                        />
                      ) : (
                        <RadioFieldGroup
                          name="type"
                          items={stockTransferTypeOptions}
                        />
                      )}
                    </Col>
                  </Row>

                  <Row>
                    <Col>
                      <Flex
                        gap={mobile ? 24 : 16}
                        flexDirection={mobile ? 'column' : 'row'}
                      >
                        <Styles.StyledSelectFields
                          onlyValue
                          isClearable
                          name="sourceStorageId"
                          placeholder="Select Source Storage"
                          onChange={() => setFieldValue('items', [])}
                          {...sourceSelectFieldProps}
                        />
                        {!mobile && (
                          <Icon.Change style={{ marginTop: '32px' }} />
                        )}
                        <Styles.StyledSelectFields
                          onlyValue
                          isClearable
                          name="destinationStorageId"
                          placeholder="Select Destination Storage"
                          {...destinationSelectFieldProps}
                        />
                      </Flex>
                    </Col>
                  </Row>

                  {values.type === StockTransferType.ClientToClient &&
                    values.sourceCompanyId && (
                      <Row>
                        <Col>
                          <RadioFieldGroup
                            name="companyTransferType"
                            items={[
                              {
                                label: 'Storage',
                                value: 'storage',
                              },
                              {
                                label: 'Event',
                                value: 'event',
                              },
                            ]}
                          />
                        </Col>
                      </Row>
                    )}
                </>
              )}

              <Row>
                {!isCompleted &&
                  values.type === StockTransferType.ClientToClient &&
                  values.sourceCompanyId && (
                    <Col col={3}>
                      <SelectField
                        onlyValue
                        name="sourceStorageId"
                        label={`From: ${values.companyTransferType === 'storage' ? 'Storage' : 'Event'}`}
                        options={
                          values.companyTransferType === 'storage'
                            ? storageSelectOptions
                            : eventSelectOptions
                        }
                      />
                    </Col>
                  )}

                <Col col={3}>
                  <DatePickerField name="date" label="Date" />
                </Col>
              </Row>

              {/*
                LTL freight on a transfer. Mirrors the Stock Order shipment
                fields (pages/inventory/stock-orders/form/order-info.tsx).
                Only the freight is captured -- the goods being moved are
                already owned, so their value is not re-entered as a cost.
              */}
              <Row>
                <Col col={3} colTablet={6}>
                  <SelectField
                    onlyValue
                    label="Carrier"
                    name="carrierId"
                    options={carrierSelectOptions}
                  />
                </Col>

                <Col col={3} colTablet={6}>
                  <Input
                    disabled
                    label="Link to Carrier Website"
                    defaultValue={
                      carriers?.find(({ id }) => id === values.carrierId)?.link
                    }
                  />
                </Col>

                {/* Russell Logistics invoice # (= Load #), matched to his ACH. */}
                <Col col={3} colTablet={6}>
                  <InputField label="LTL Invoice #" name="ltlInvoiceNumber" />
                </Col>

                <Col col={3} colTablet={6}>
                  <InputField
                    number
                    decimal
                    name="ltlPrice"
                    label="LTL Price"
                  />
                </Col>
              </Row>

              {values.type === StockTransferType.StorageToStorage && (
                <Suspense fallback={null}>
                  <TransferEventSplit
                    savedEvents={props.initialValues?.eventAllocations}
                  />
                </Suspense>
              )}

              {values.sourceStorageId && (
                <StorageProductsSelectSection
                  storageId={values.sourceStorageId}
                />
              )}
            </CommonStyles.FormInner>

            <FormFooter
              right={
                <CommonStyles.FormButtons>
                  <Button
                    text="Cancel"
                    fullWidth={mobile}
                    onClick={() => navigate(-1)}
                    colorType={ButtonColors.LightBlue}
                  />
                  <Button
                    fullWidth={mobile}
                    type={ButtonTypes.Submit}
                    disabled={!dirty || isSubmitting}
                    text={isEdit ? 'Edit' : 'Create'}
                  />
                </CommonStyles.FormButtons>
              }
            />
          </CommonStyles.Form>
        );
      }}
    </Formik>
  );
};
